import {
  Component,
  h,
  Element,
  Prop,
  State,
  Listen,
  Method,
  Event,
  EventEmitter,
  Watch,
} from "@stencil/core"
import {
  menuFlyoutReducer,
  getInitialState,
  MenuFlyoutState,
  MenuFlyoutActions,
} from "./menu-flyout-store"
import {
  StoreConnection,
  StateHandle,
  parseFunction,
  getOverlayOutletEl,
  getScrollContainerEl,
  getScreenOrientationAngle,
  modifyOverlayOutlet,
  getScrollTopLeft,
  animation,
} from "../../core/helpers/webcomponent-helpers"
import { Display } from "../../core/types/types"

export type Direction =
  | "bottom-right"
  | "bottom-left"
  | "top-right"
  | "top-left"

@Component({
  tag: "sdx-menu-flyout",
  styleUrl: "menu-flyout.scss",
  shadow: true,
})
export class MenuFlyout {
  private store: StoreConnection<this, MenuFlyoutState, MenuFlyoutActions>
  private isConnected = false
  private invokeDisplayChangeCallback: Function = () => null
  private overlayOutletEl?: Element
  private scrollContainerEl?: Element
  private animationDuration = 300

  private oppositeDirection: {
    x: { [key in Direction]: Direction }
    y: { [key in Direction]: Direction }
  } = {
    x: {
      "top-right": "top-left",
      "top-left": "top-right",
      "bottom-right": "bottom-left",
      "bottom-left": "bottom-right",
    },
    y: {
      "top-right": "bottom-right",
      "top-left": "bottom-left",
      "bottom-right": "top-right",
      "bottom-left": "top-left",
    },
  }

  /**
   * The arrow is basically a 14px square (rotated by 45°).
   */
  private arrowUnrotatedWidth = 14

  /**
   * Horizontal distance between contentEl and arrowEl.
   */
  private offsetX = 24

  /**
   * Vertical distance between toggleEl and arrowEl (and, on mobiles, to the
   * screen).
   */
  private offsetY = 16

  private lastScreenOrientationAngle = 0

  @Element() public el!: HTMLSdxMenuFlyoutElement

  @State() public state!: StateHandle<MenuFlyoutState>

  /**
   * In which direction the flyout opens. This is probably only needed in rare
   * cases, because the flyout will automatically open itself towards the
   * opposite direction if there’s not enough space.
   */
  @Prop() public direction: Direction = "bottom-right"

  /**
   * Close if the user clicks on the flyout.
   */
  @Prop() public closeOnClick: boolean = false

  /**
   * Callback that will fire after the flyouts display status has changed.
   * @private Needed in sdx-input.
   */
  @Prop() public displayChangeCallback?: ((display: Display) => void) | string

  /**
   * Disable animations for testing.
   * @private
   */
  @Prop() public animated: boolean = true

  /**
   * Instead of calculating the position based on the position of the host
   * element, use this external reference element.
   * Used in sdx-tour.
   * @private
   */
  @Prop() public referenceEl?: HTMLElement

  /**
   * Emitted when the component’s visibility updates.
   */
  @Event() public sdxdisplaychange!: EventEmitter<{
    display: "open" | "opening" | "closed" | "closing"
  }>

  @Watch("state")
  public stateChanged({}, prev: StateHandle<MenuFlyoutState>) {
    const state = this.state.get()
    const prevState = prev.get()

    if (state.display !== prevState.display) {
      this.displayChanged(state)
    }

    if (state.arrowEls !== prevState.arrowEls) {
      this.arrowElsChanged()
    }
  }

  @Watch("direction")
  public directionChanged() {
    const { display } = this.state.get()

    if (display !== "closed") {
      // If the flyout is open, re-open it with new direction
      this.close().then(() => {
        this.store.set("direction", this.direction)
        this.open()
      })
    } else {
      this.store.set("direction", this.direction)
    }
  }

  @Watch("displayChangeCallback")
  public displayChangeCallbackChanged() {
    this.setInvokeDisplayChangeCallback()
  }

  @Watch("referenceEl")
  public referenceElChanged() {
    this.positionContentEl(this.direction)
  }

  @Listen("resize", { target: "window" })
  public onWindowResize() {
    const { display, direction } = this.state.get()

    if (display === "closed") {
      return
    }

    const currentAngle = getScreenOrientationAngle()

    if (currentAngle !== this.lastScreenOrientationAngle) {
      // just close in case of an orientation change
      this.lastScreenOrientationAngle = currentAngle
      this.close()
    } else {
      // in case of a desktop browser resize just recalculate the position
      this.positionContentEl(direction)
    }
  }

  /**
   * Toggles the flyout.
   */
  @Method()
  public toggle(): Promise<void> {
    const { display } = this.state.get()

    if (display === "open" || display === "opening") {
      return this.close()
    }

    return this.open()
  }

  /**
   * Opens the flyout.
   */
  @Method()
  public open(): Promise<void> {
    return new Promise((resolve) => {
      const { display, direction, contentEl, isUserInteractionInProgress } =
        this.state.get()

      if (!contentEl) {
        return
      }

      // Only a closed flyout can be opened
      if (display === "open" || display === "opening") {
        resolve()
        return
      }

      this.overlayOutletEl = getOverlayOutletEl()
      this.scrollContainerEl = getScrollContainerEl(this.el)

      // Check if the flyout is inside a scrollable element.
      // If yes, append the list there to make sure it scrolls and isn't
      // detached from the toggle.
      if (this.scrollContainerEl) {
        this.overlayOutletEl = this.scrollContainerEl
      } else {
        this.scrollContainerEl = document.documentElement
      }

      // Make contentEl overlap all other content
      modifyOverlayOutlet("add", contentEl, this.overlayOutletEl, this.el)

      animation.set(contentEl, {
        opacity: "0",
      })

      this.positionContentEl(direction)

      this.setDisplay("opening", isUserInteractionInProgress)

      const animationStartingPosition =
        this.state.get().direction === "top-left" ||
        this.state.get().direction === "top-right"
          ? this.offsetY
          : -this.offsetY

      animation.set(contentEl, {
        transform: `translateY(${animationStartingPosition}px)`,
      })

      animation.start(
        contentEl,
        {
          transform: "translateY(0)",
          opacity: "1",
        },
        this.animated ? this.animationDuration : 0,
        {
          finished: () => {
            this.setDisplay("open", isUserInteractionInProgress)
            resolve()
          },
        },
      )
    })
  }

  /**
   * Closes the flyout.
   */
  @Method()
  public close(): Promise<void> {
    return new Promise((resolve) => {
      const { display, contentEl, isUserInteractionInProgress } =
        this.state.get()

      if (!contentEl) {
        return
      }

      // Only an opened flyout can be closed
      if (display === "closed" || display === "closing") {
        resolve()
        return
      }

      this.setDisplay("closing", isUserInteractionInProgress)

      animation.start(
        contentEl,
        {
          transform: "translateY(0)",
          opacity: "0",
        },
        this.animated ? this.animationDuration : 0,
        {
          finished: () => {
            animation.set(contentEl, { display: "none" })

            // Reset to original direction (it might have changed during opening
            // because of not enough space)
            this.store.set("direction", this.direction)

            this.setDisplay("closed", isUserInteractionInProgress)

            // Move contentEl back
            modifyOverlayOutlet("remove", contentEl, this.el, this.el)

            resolve()
          },
        },
      )
    })
  }

  /**
   * @private
   * Calls close(), but only when the component is allowed to be closed.
   */
  @Method()
  public async requestToClose(
    e: KeyboardEvent | MouseEvent | TouchEvent,
  ): Promise<void> {
    const { toggleEl, contentEl } = this.state.get()

    if (!contentEl) {
      return
    }

    if (e instanceof KeyboardEvent) {
      if (e.key === "Escape") {
        this.store.set("isUserInteractionInProgress", true)
        this.close()
        this.store.set("isUserInteractionInProgress", false)
      }

      return
    }

    const didClickOnToggleEl = toggleEl && e.composedPath().includes(toggleEl)
    const didClickOnContentEl = e.composedPath().includes(contentEl)

    if (didClickOnToggleEl) {
      return
    }

    if (didClickOnContentEl) {
      if (this.closeOnClick) {
        this.store.set("isUserInteractionInProgress", true)
        this.close()
        this.store.set("isUserInteractionInProgress", false)
      }

      return
    }

    // Clicked somewhere else on window
    this.store.set("isUserInteractionInProgress", true)
    this.close()
    this.store.set("isUserInteractionInProgress", false)
  }

  constructor() {
    this.setInvokeDisplayChangeCallback()

    this.store = new StoreConnection(
      this,
      menuFlyoutReducer,
      getInitialState(),
      ["display", "direction", "contentEl", "toggleEl", "arrowEls"],
    )

    this.store.set("direction", this.direction)
    this.store.set("toggle", this.toggle.bind(this))

    this.store.flush()
  }

  public connectedCallback() {
    this.isConnected = true
  }

  public disconnectedCallback() {
    this.isConnected = false

    // Make sure the flyout is closed when removed from the DOM
    this.close()
  }

  public componentDidLoad() {
    this.store.subscribe()
  }

  private displayChanged(state: MenuFlyoutState) {
    this.invokeDisplayChangeCallback(state.display)
  }

  private setDisplay(
    display: Display,
    isUserInteractionInProgress: boolean,
  ): void {
    this.store.set("display", display)

    // Only fire events on end user interaction
    if (!isUserInteractionInProgress) {
      return
    }

    this.sdxdisplaychange.emit({ display })
  }

  private arrowElsChanged() {
    if (!this.isConnected) {
      return
    }

    this.positionArrowEls()
  }

  private setInvokeDisplayChangeCallback(): void {
    this.invokeDisplayChangeCallback = parseFunction(this.displayChangeCallback)
  }

  private positionContentEl(direction: Direction): void {
    const { contentEl } = this.state.get()

    if (!(contentEl && this.scrollContainerEl)) {
      return
    }

    // base of calculations is an external reference element or the host element
    const referenceElRect = this.referenceEl?.getBoundingClientRect()
    const elRect = referenceElRect || this.el.getBoundingClientRect()
    const elHalfWidth = elRect.width / 2

    const isDirection = {
      bottom: direction === "bottom-right" || direction === "bottom-left",
      left: direction === "top-left" || direction === "bottom-left",
      right: direction === "top-right" || direction === "bottom-right",
      top: direction === "top-right" || direction === "top-left",
    }

    let spaceTowards = {
      bottom: document.documentElement.clientHeight - elRect.bottom,
      left: elRect.left + elHalfWidth,
      right: document.documentElement.clientWidth - elRect.right + elHalfWidth,
      top: elRect.top,
    }

    let scrollContainerElRect = {
      bottom: 0,
      left: 0,
      right: 0,
      top: 0,
    }

    // If the overlay is inside an offset parent, include all its spacings
    // in the calculation.
    if (this.scrollContainerEl !== document.documentElement) {
      scrollContainerElRect = this.scrollContainerEl.getBoundingClientRect()

      // Subtract elRect from scrollContainerElRect
      spaceTowards = {
        bottom: scrollContainerElRect.bottom - elRect.bottom,
        left: elRect.left + elHalfWidth - scrollContainerElRect.left,
        right: scrollContainerElRect.right - elRect.right + elHalfWidth,
        top: elRect.top - scrollContainerElRect.top,
      }
    }

    // Show the contentEl to take measurements
    contentEl.style.display = "block"

    const contentElWidth = contentEl.clientWidth
    const contentElHeight = contentEl.clientHeight

    const hasEnoughSpaceTo = {
      bottom: spaceTowards.bottom >= contentElHeight + this.arrowUnrotatedWidth,
      left: spaceTowards.left >= contentElWidth,
      right: spaceTowards.right >= contentElWidth,
      top: spaceTowards.top >= contentElHeight + this.arrowUnrotatedWidth,
    }

    const bottom =
      spaceTowards.top +
      getScrollTopLeft(this.scrollContainerEl).scrollTop +
      elRect.height +
      this.offsetY

    const left =
      spaceTowards.left +
      getScrollTopLeft(this.scrollContainerEl).scrollLeft -
      contentElWidth +
      this.offsetX +
      this.arrowUnrotatedWidth / 2

    const right =
      spaceTowards.left +
      getScrollTopLeft(this.scrollContainerEl).scrollLeft -
      this.offsetX -
      this.arrowUnrotatedWidth / 2

    const top =
      spaceTowards.top +
      getScrollTopLeft(this.scrollContainerEl).scrollTop -
      contentElHeight -
      this.offsetY

    let currentDirection = direction

    // Place the contentEl
    const fallbackLeft = left < 0 ? 0 : left

    if (isDirection.left) {
      if (hasEnoughSpaceTo.left) {
        contentEl.style.left = `${left}px`
      } else {
        if (hasEnoughSpaceTo.right) {
          contentEl.style.left = `${right}px`
          currentDirection = this.oppositeDirection.x[currentDirection]
        } else {
          contentEl.style.left = `${fallbackLeft}px`
        }
      }
    }

    if (isDirection.right) {
      if (hasEnoughSpaceTo.right) {
        contentEl.style.left = `${right}px`
      } else {
        contentEl.style.left = `${fallbackLeft}px`
        currentDirection = this.oppositeDirection.x[currentDirection]
      }
    }

    if (isDirection.top) {
      if (hasEnoughSpaceTo.top) {
        contentEl.style.top = `${top}px`
      } else {
        if (hasEnoughSpaceTo.bottom) {
          contentEl.style.top = `${bottom}px`
          currentDirection = this.oppositeDirection.y[currentDirection]
        } else {
          contentEl.style.top = `${top}px`
        }
      }
    }

    if (isDirection.bottom) {
      if (hasEnoughSpaceTo.bottom) {
        contentEl.style.top = `${bottom}px`
      } else {
        if (hasEnoughSpaceTo.top) {
          contentEl.style.top = `${top}px`
          currentDirection = this.oppositeDirection.y[currentDirection]
        } else {
          contentEl.style.top = `${bottom}px`
        }
      }
    }

    // Update direction
    this.store.set("direction", currentDirection)

    this.positionArrowEls(parseFloat(contentEl.style.left))
  }

  private positionArrowEls(newLeftPossiblyNotAdoptedYet?: number) {
    const { contentEl, toggleEl, arrowEls } = this.state.get()

    // if contentEl stays open (sdx-tour) the new left style is not adopted yet and therefore the rect is wrong
    const left =
      newLeftPossiblyNotAdoptedYet || contentEl!.getBoundingClientRect().left

    const scrollContainerElLeft =
      this.scrollContainerEl?.getBoundingClientRect().left || 0
    const toggleElRect = toggleEl?.getBoundingClientRect()
    const referenceElRect = this.referenceEl?.getBoundingClientRect()
    const hostElRect = this.el.getBoundingClientRect()
    const alignArrowToRect = referenceElRect || toggleElRect || hostElRect

    arrowEls.forEach((arrowEl) => {
      arrowEl.style.left = `${
        alignArrowToRect.left -
        left -
        scrollContainerElLeft +
        alignArrowToRect.width / 2 -
        this.arrowUnrotatedWidth / 2
      }px`
    })
  }

  public render() {
    return <slot />
  }
}
